import React from 'react';
import styled, { keyframes } from 'styled-components';
import logoImage from '../assets/logo.jpg';

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: #F8F9FA;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const pulse = keyframes`
  0%, 100% { 
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.3);
  }
  50% { 
    box-shadow: 0 12px 30px rgba(212, 175, 55, 0.5);
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const Logo = styled.img`
  width: 180px;
  height: 180px;
  object-fit: cover;
  border-radius: 50%;
  background: white;
  padding: 15px;
  box-shadow: 0 10px 30px rgba(212, 175, 55, 0.3);
  animation: ${pulse} 2s ease-in-out infinite;
  
  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    width: 120px;
    height: 120px;
    padding: 10px;
  }
`;

const IronContainer = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  max-width: 480px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
    transform: scale(0.8);
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 30px;
    transform: scale(0.7);
    max-width: 320px;
  }
`;


// Keyframe animations
const ironMove = keyframes`
  0% {
    left: 0;
  }
  50% {
    left: 24em;
  }
  100% {
    left: 0;
  }
`;

const ironMoveMobile = keyframes`
  0% {
    left: 0;
  }
  50% {
    left: 20em;
  }
  100% {
    left: 0;
  }
`;

const clothLoad = keyframes`
  0% {
    width: 0;
  }
  50% {
    width: 24em;
  }
  100% {
    width: 0;
  }
`;

const clothLoadMobile = keyframes`
  0% {
    width: 0;
  }
  50% {
    width: 20em;
  }
  100% {
    width: 0;
  }
`;

const steamAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.75, 0.75);
  }
  10% {
    opacity: 0.5;
    transform: scale(1.1, 1.1);
  }
  20% {
    opacity: 0;
    transform: scale(0.75, 0.75);
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

const IroningContainer = styled.div`
  position: relative;
  width: 40em;
  height: 13em;
  background-color: transparent;
  overflow: hidden;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    width: 35em;
    height: 11em;
  }
  
  @media (max-width: 480px) {
    width: 30em;
    height: 10em;
  }
`;

const Cloth = styled.div`
  position: absolute;
  bottom: 3em;
  width: 30em;
  border-bottom: 0.1em solid rgba(212, 175, 55, 0.5);
  animation: ${clothLoad} 3s cubic-bezier(0.87, 0.30, 0.45, 1) infinite;
  
  @media (max-width: 480px) {
    animation: ${clothLoadMobile} 3s cubic-bezier(0.87, 0.30, 0.45, 1) infinite;
  }
`;

const Iron = styled.div`
  position: absolute;
  bottom: 2em;
  left: 0;
  width: 6em;
  height: 5em;
  background-color: #F8F9FA;
  overflow: hidden;
  animation: ${ironMove} 3s cubic-bezier(0.87, 0.30, 0.45, 1) infinite;
  
  @media (max-width: 480px) {
    animation: ${ironMoveMobile} 3s cubic-bezier(0.87, 0.30, 0.45, 1) infinite;
  }
`;

const SteamCloud = styled.div`
  position: absolute;
  top: 0.5em;
  left: 3.8em;
  width: 2em;
  height: 2em;
  // background-color: #d9d9d9;
  background-color: transparent;
  opacity: 0;
  animation: ${steamAnimation} 2s ease-in-out 1s infinite;
`;

const Stripe1 = styled.div`
  position: absolute;
  left: -0.1em;
  bottom: 0.5em;
  width: 0.8em;
  height: 0.2em;
  background-color: #D4AF37;
  transform: rotate(-70deg) skew(-50deg);
`;

const Stripe2 = styled.div`
  position: absolute;
  left: 0.2em;
  bottom: 0.1em;
  width: 0.9em;
  height: 0.2em;
  background-color: #D4AF37;
  transform: rotate(-25deg) skew(60deg);
`;

const Puff1 = styled.div`
  position: absolute;
  left: 0.3em;
  bottom: 1em;
  width: 0.6em;
  height: 0.6em;
  background-color: #F8F9FA;
  border-left: 0.2em solid #D4AF37;
  border-top: 0.2em solid #D4AF37;
  border-bottom: 0.2em solid #D4AF37;
  border-radius: 50%;
  transform: rotate(40deg) scale(1.2, 1.1);
`;

const Puff2 = styled.div`
  position: absolute;
  left: 0.85em;
  bottom: 1em;
  width: 0.6em;
  height: 0.6em;
  background-color: #F8F9FA;
  border-left: 0.2em solid #D4AF37;
  border-top: 0.2em solid #D4AF37;
  border-right: 0.2em solid transparent;
  border-radius: 50%;
  transform: rotate(80deg) scale(1.2, 1.2);
`;

const Puff3 = styled.div`
  position: absolute;
  left: 1.2em;
  bottom: 0.3em;
  width: 0.6em;
  height: 0.6em;
  background-color: #F8F9FA;
  border-top: 0.2em solid #D4AF37;
  border-right: 0.2em solid #D4AF37;
  border-bottom: 0.2em solid #D4AF37;
  border-radius: 50%;
  transform: rotate(-10deg) scale(1.2, 1.1);
`;

const Curve = styled.div`
  position: absolute;
  width: 10em;
  height: 10em;
  margin-top: 0.5em;
  margin-left: -4.7em;
  background-color: #D4AF37;
  border-radius: 50%;
  transform: rotateX(30deg);
  overflow: hidden;
`;

const Grip = styled.div`
  position: relative;
  top: 1.8em;
  left: 1em;
  width: 2em;
  height: 1.2em;
  background-color: #F8F9FA;
  border-radius: 35% 45% 20% 15%;
  transform: rotate(15deg);
  
  &::before {
    position: absolute;
    content: '';
    bottom: 0.35em;
    left: 0.65em;
    width: 0.75em;
    height: 0.15em;
    border-radius: 30% 30% 10% 10%;
    background-color: #D4AF37;
    transform: rotate(-15deg);
  }
  
  &::after {
    position: absolute;
    content: '';
    bottom: -0.7em;
    left: 0;
    height: 1em;
    width: 2.5em;
    background-color: #D4AF37;
    transform: rotate(-15deg);
  }
`;

const Hide1 = styled.div`
  position: absolute;
  top: 0.75em;
  left: -0.6em;
  width: 1em;
  height: 3em;
  background-color: #F8F9FA;
  transform: rotate(25deg);
`;

const Hide2 = styled.div`
  position: absolute;
  top: 3em;
  left: -1.2em;
  width: 2em;
  height: 2em;
  border-radius: 50%;
  background-color: #F8F9FA;
  transform: rotateY(30deg);
`;

const Hide3 = styled.div`
  position: absolute;
  bottom: -0.25em;
  right: 0;
  width: 6em;
  height: 1.25em;
  background-color: #F8F9FA;
`;

const Part1 = styled.div`
  position: absolute;
  top: 2.25em;
  right: 1.9em;
  width: 1em;
  height: 1em;
  background-color: #D4AF37;
  border-radius: 35%;
  transform: rotate(-5deg);
`;

const Part2 = styled.div`
  position: absolute;
  left: 0.6em;
  bottom: 1.6em;
  width: 0.5em;
  height: 0.5em;
  background-color: #F8F9FA;
  border-radius: 50%;
`;

const Basis = styled.div`
  position: absolute;
  bottom: 1.15em;
  left: 0;
  width: 5.5em;
  height: 0.12em;
  background-color: #F8F9FA;
  transform: rotateX(20deg);
`;




// Loading text
const LoadingText = styled.div`
  margin-top: 40px;
  font-size: 18px;
  color: #6B7280;
  font-weight: 400;
  animation: ${blink} 1.6s infinite;
  letter-spacing: 2px;
  text-transform: lowercase;
  font-style: italic;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-top: 30px;
    letter-spacing: 1.5px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-top: 20px;
    letter-spacing: 1px;
  }
`;

// Company name
const CompanyName = styled.div`
  margin-top: 15px;
  font-size: 42px;
  color: #D4AF37;
  font-weight: 800;
  letter-spacing: 8px;
  text-shadow: 0 4px 12px rgba(212, 175, 55, 0.25);
  font-family: 'Crimson Text', 'Copperplate', 'Copperplate Gothic Light', 'Arial Black', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 32px;
    letter-spacing: 6px;
    margin-top: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    letter-spacing: 4px;
    margin-top: 10px;
  }
`;

const LoadingScreen = () => {
  return (
    <LoadingContainer>
      <LogoContainer>
        <Logo src={logoImage} alt="Istri Wala Logo" />
      </LogoContainer>
      
      <IronContainer>
        {/* Iron Animation */}
        <IroningContainer>
          <Cloth />
          <Iron>
            <SteamCloud>
              <Stripe1 />
              <Stripe2 />
              <Puff1 />
              <Puff2 />
              <Puff3 />
            </SteamCloud>
            <Curve />
            <Grip />
            <Part1 />
            <Part2 />
            <Hide1 />
            <Hide2 />
            <Hide3 />
            <Basis />
          </Iron>
        </IroningContainer>
      </IronContainer>
      
      <LoadingText>
        Ironing Perfection…
      </LoadingText>
      
      <CompanyName>
        Istri Wala
      </CompanyName>
    </LoadingContainer>
  );
};

export default LoadingScreen;
